<template>
  <component
    :is="allowDrop ? Drop : 'tr'"
    v-bind="allowDrop ? { tag: 'tr' } : {}"
    :class="rowClassList"
    @click="onRowClick"
    @drop="emit('drop', $event)"
    @dragover="emit('dragover', $event)"
  >
    <td
      v-for="(col, index) in columns"
      :key="col.key"
      :class="{ 'no-print': col.isActions }"
    >
      <component
        :is="cellWrapper(col, index)"
        v-bind="cellWrapperProps(col, index)"
      >
        <div
          v-if="index === 0"
          class="bm-grid-row-controls"
          :style="{ marginLeft: firstColumnIndentPx + 'px' }"
        >
          <bm-grid-drag-handle
            v-if="allowDrag && rowDraggable(row)"
            :drag-data="draggableItems"
            @drag-start="emit('drag-start')"
          />
          <bm-grid-row-expander
            v-if="allowExpand && rowExpandable(row)"
            :is-expanded="row.isExpanded"
            @toggle="emit('toggle-expand')"
          />
          <bm-grid-row-selector
            v-if="allowSelect"
            :checked="row.isSelected && rowSelectable(row)"
            :disabled="!rowSelectable(row)"
            :is-header="false"
            @change="emit('toggle-select')"
          />
          <bm-grid-content-cell :column="col.column" :item="row.item" />
        </div>
        <bm-grid-content-cell v-else :column="col.column" :item="row.item" />
      </component>
    </td>
  </component>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type {
  BmGridColumnConfiguration,
  BmGridColumnConfigurations,
  ContentRow,
} from "@/components/controls/grid/BmGridData";
import BmGridContentCell from "@/components/controls/grid/BmGridContentCell.vue";
import BmGridDragHandle from "@/components/controls/grid/BmGridDragHandle.vue";
import BmGridRowExpander from "@/components/controls/grid/BmGridRowExpander.vue";
import BmGridRowSelector from "@/components/controls/grid/BmGridRowSelector.vue";
import type { DnDEvent } from "vue-easy-dnd";
import { Drop } from "vue-easy-dnd";
import type { Location } from "vue-router";
import type { Maybe } from "@/components/utils/typeHelpers";

/** Vue 2 functional passthrough so non-linked cells keep a single root without an extra DOM node. */
const CellPassthrough = {
  name: "BmGridTableContentRowCellPassthrough",
  functional: true,
  render: (_h: unknown, ctx: { children: unknown }) => ctx.children,
};

interface BmGridTableContentRowProps {
  row: ContentRow;
  rowIndex: number;
  visibleColumns: BmGridColumnConfigurations;
  allowDrag: boolean;
  allowDrop: boolean;
  allowExpand: boolean;
  allowSelect: boolean;
  draggableItems: readonly string[];
  firstColumnIndentPx: number;
  rowDraggable: (item: any) => boolean;
  rowExpandable: (item: any) => boolean;
  rowSelectable: (item: any) => boolean;
  rowClasses: (row: any) => string[];
  rowClickRoute?: Maybe<(item: any) => Maybe<Location>>;
  isRowClickable: boolean;
  allowRowClickRouteOnFirstCell: boolean;
}

const props = defineProps<BmGridTableContentRowProps>();

interface BmGridTableContentRowEvents {
  (event: "drag-start"): void;
  (event: "toggle-expand"): void;
  (event: "toggle-select"): void;
  (event: "drop", eventPayload: DnDEvent): void;
  (event: "dragover", eventPayload: DnDEvent): void;
  (event: "row-click"): void;
}

const emit = defineEmits<BmGridTableContentRowEvents>();

type ColumnEntry = {
  key: string;
  column: BmGridColumnConfiguration;
  isActions: boolean;
};

const columns = computed((): ColumnEntry[] =>
  Object.entries(props.visibleColumns).map(([key, column]) => ({
    key,
    column,
    isActions: !!column.rendererMetadata.actions,
  }))
);

const rowClassList = computed(() => {
  const classes: Record<string, boolean> = {
    content: true,
    selected: props.row.isSelected,
    even: props.rowIndex % 2 === 0,
    "row-clickable": props.isRowClickable,
    "drop-below": props.row.dropBelow,
  };
  props.rowClasses(props.row).forEach((newClass) => {
    classes[newClass] = true;
  });
  return classes;
});

function isActionsColumn(column: BmGridColumnConfiguration): boolean {
  return !!column.rendererMetadata.actions;
}

function isCellClickable(index: number): boolean {
  return index !== 0 ? true : props.allowRowClickRouteOnFirstCell;
}

function shouldWrapCell(col: ColumnEntry, index: number): boolean {
  return (
    isCellClickable(index) &&
    props.isRowClickable &&
    !isActionsColumn(col.column)
  );
}

function cellWrapper(col: ColumnEntry, index: number) {
  if (!shouldWrapCell(col, index)) {
    return CellPassthrough;
  }
  const routeLocation = props.rowClickRoute
    ? props.rowClickRoute(props.row.item)
    : null;
  return routeLocation ? "router-link" : "a";
}

function cellWrapperProps(col: ColumnEntry, index: number) {
  if (!shouldWrapCell(col, index)) {
    return {};
  }
  const routeLocation = props.rowClickRoute
    ? props.rowClickRoute(props.row.item)
    : null;
  return routeLocation ? { to: routeLocation } : {};
}

function onRowClick() {
  if (props.isRowClickable) {
    emit("row-click");
  }
}
</script>

<style scoped lang="sass">
@import "src/sass/typography"
@import "src/sass/colours"

@mixin contents-text
  @include bm-body2
  color: $bm-color-text01

// Remove a-tag styling for the grid rows
.row-clickable a
  text-decoration: none
  color: $bm-color-text01

tr, :deep(tr)
  height: 36px

td
  @include contents-text
  border-bottom: 1px solid #D1CFD7

.content.even
  background-color: $bm-color-background01

.content:not(.even)
  background-color: $bm-color-background04

.bm-grid-row-controls
  display: flex
  align-items: center

.row-clickable td
  cursor: pointer
  transition: background .1s ease-in-out

.row-clickable:hover td
  background: $bm-color-text03

// Client utility row classes
tr.grid-row-error
  background-color: $bm-color-incomplete-row !important

tr.grid-row-warn
  background-color: $bm-color-warn-row !important

tr.grid-row-success
  background-color: $bm-color-successful-row !important

// vue-easy-dnd adds .drop-in on the Drop root while dragging over —
// must live here because BmGrid scoped .drop-in no longer reaches these tds.
.drop-in td, :deep(.drop-in) td
  border-bottom: 3px solid
  border-top: inherit
  transition: 0.1s

.drop-in.drop-above td, :deep(.drop-in.drop-above) td
  border-top: 3px solid
  border-bottom: inherit
  transition: 0.1s
</style>
